import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { Terminal as XTerm } from "xterm";
import { FitAddon } from "@xterm/addon-fit";
import "xterm/css/xterm.css";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  Chip,
} from "@mui/material";
import {
  ArrowBack,
  Terminal as TerminalIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  FiberManualRecord as DotIcon,
} from "@mui/icons-material";

export default function Terminal() {
  const terminalContainerRef = useRef(null);
  const terminalRef = useRef(null);

  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const socketRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [connected, setConnected] = useState(false);
  const [isReadyToMount, setIsReadyToMount] = useState(false);

  // Kopyala/Yapıştır bildirimleri için
  const [snackbarMsg, setSnackbarMsg] = useState("");

  const serverId = location.state?.serverId;
  const serverName = location.state?.serverName || "Bilinmeyen Sunucu";

  // Pano İşlemleri (Kopyala / Yapıştır) - MANTIĞA DOKUNULMADI
  const handleManualCopy = () => {
    if (xtermRef.current) {
      const selection = xtermRef.current.getSelection();
      if (selection) {
        navigator.clipboard.writeText(selection);
        setSnackbarMsg("Metin kopyalandı!");
      } else {
        setSnackbarMsg("Lütfen önce kopyalanacak metni seçin.");
      }
    }
  };

  const handleManualPaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (socketRef.current && text) {
        socketRef.current.emit("ssh-input", text);
        setSnackbarMsg("Panodan yapıştırıldı!");
      }
    } catch (err) {
      setSnackbarMsg("Pano izni reddedildi veya boş.");
    }
  };

  // 1. AŞAMA: Kapsayıcı Boyut Ölçümü - MANTIĞA DOKUNULMADI
  useEffect(() => {
    if (!terminalContainerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          setIsReadyToMount(true);
          observer.disconnect();
        }
      }
    });

    observer.observe(terminalContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // 2. AŞAMA: Terminal Başlatma - SADECE TEMA RENKLERİ GÜNCELLENDİ
  useEffect(() => {
    if (!serverId) {
      setError(
        "Sunucu ID'si bulunamadı. Lütfen Dashboard'dan tekrar giriş yapın.",
      );
      return;
    }

    if (!isReadyToMount || !terminalRef.current) return;

    if (xtermRef.current) xtermRef.current.dispose();
    if (socketRef.current) socketRef.current.disconnect();

    // SADECE GÖRÜNÜM: xterm.js teması Visione renklerine uyarlandı
    const term = new XTerm({
      cursorBlink: true,
      theme: {
        background: "#030305", // Daha derin siyah
        foreground: "#eaf0f8", // ink - marka metin rengi
        cursor: "#2da7ff", // Camgöbeği imleç
        selectionBackground: "rgba(29, 111, 242, 0.4)",
      },
      fontFamily: "'Fira Code', 'Courier New', monospace",
      fontSize: 14,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(terminalRef.current);

    setTimeout(() => {
      try {
        fitAddon.fit();
      } catch (e) {}
    }, 10);

    // --- AKILLI KLAVYE YAKALAYICI (Smart Copy/Paste) --- MANTIĞA DOKUNULMADI
    term.attachCustomKeyEventHandler((e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.code === "KeyC" &&
        e.type === "keydown"
      ) {
        const selection = term.getSelection();
        if (selection) {
          navigator.clipboard.writeText(selection);
          setSnackbarMsg("Kopyalandı!");
          return false;
        }
        return true;
      }
      return true;
    });

    // --- PUUTY STİLİ OTOMATİK KOPYALAMA --- MANTIĞA DOKUNULMADI
    term.onSelectionChange(() => {
      const selection = term.getSelection();
      if (selection) {
        navigator.clipboard.writeText(selection);
      }
    });

    // --- SAĞ TIK İLE YAPIŞTIRMA --- MANTIĞA DOKUNULMADI
    const handleContextMenu = async (e) => {
      e.preventDefault();
      try {
        const text = await navigator.clipboard.readText();
        if (text) {
          socketRef.current.emit("ssh-input", text);
          setSnackbarMsg("Yapıştırıldı!");
        }
      } catch (err) {}
    };
    terminalRef.current.addEventListener("contextmenu", handleContextMenu);

    term.writeln(`Bağlantı kuruluyor: ${serverName}...`);

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    // Terminal soketi artik kimlik dogrulamasi istiyor: token el sikismada
    // gonderilir, backend gecersizse baglantiyi hic kabul etmez.
    const socket = io({ auth: { token: localStorage.getItem("adminToken") } });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("start-ssh", { serverId });
    });

    socket.on("connect_error", (err) => {
      const message =
        err?.message === "Yetkisiz erisim"
          ? "Oturumunuz gecersiz. Lutfen tekrar giris yapin."
          : `Baglanti kurulamadi: ${err?.message || "bilinmeyen hata"}`;
      term.writeln(`\r\n\x1b[31m[HATA]\x1b[0m ${message}\r\n`);
      setError(message);
    });

    socket.on("ssh-connected", () => {
      setConnected(true);
      term.writeln(
        "\r\n\x1b[32m[BAŞARILI]\x1b[0m Güvenli SSH bağlantısı kuruldu.\r\n",
      );
    });

    socket.on("ssh-data", (data) => term.write(data));

    socket.on("ssh-error", (errMsg) => {
      term.writeln(`\r\n\x1b[31m[HATA]\x1b[0m ${errMsg}\r\n`);
      setError(errMsg);
    });

    term.onData((data) => socket.emit("ssh-input", data));

    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (fitAddonRef.current) {
          try {
            fitAddonRef.current.fit();
          } catch (e) {}
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
      if (terminalRef.current)
        terminalRef.current.removeEventListener(
          "contextmenu",
          handleContextMenu,
        );
      if (socketRef.current) socketRef.current.disconnect();
      if (xtermRef.current) xtermRef.current.dispose();
    };
  }, [serverId, serverName, isReadyToMount]);

  return (
    <Box
      sx={{ height: "85vh", display: "flex", flexDirection: "column", gap: 1 }}
    >
      {/* ÜST BAR VE BUTONLAR - GÖRÜNÜM GÜNCELLENDİ */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate("/")}
            sx={{
              bgcolor: "rgba(234,240,248,0.03)",
              "&:hover": { bgcolor: "rgba(29,111,242,0.1)" },
            }}
          >
            <ArrowBack sx={{ color: "text.primary" }} />
          </IconButton>
          <TerminalIcon sx={{ color: "primary.main", fontSize: 28 }} />
          <Typography sx={{ fontWeight: "800", display: "flex", alignItems: "center", gap: 1 }}
            variant="h5"
          >
            {serverName}
            <Chip
              label="SSH"
              size="small"
              sx={{
                bgcolor: "rgba(29, 111, 242, 0.15)",
                color: "primary.light",
                fontWeight: "bold",
                borderRadius: 1.5,
              }}
            />
          </Typography>
          {!connected && !error && (
            <CircularProgress
              size={20}
              sx={{ ml: 2, color: "primary.light" }}
            />
          )}
        </Box>

        {/* Aksiyon Butonları (Mobilde Icon, Masaüstünde Metinli) - GÖRÜNÜM GÜNCELLENDİ */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CopyIcon />}
            onClick={handleManualCopy}
            sx={{
              borderColor: "rgba(234,240,248,0.1)",
              color: "text.secondary",
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "block" } }}>Kopyala</Box>
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PasteIcon />}
            onClick={handleManualPaste}
            sx={{
              background: "linear-gradient(135deg, #2da7ff 0%, #1d6ff2 100%)",
              color: "#fff",
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "block" } }}>Yapıştır</Box>
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1, borderRadius: 2, bgcolor: "rgba(244,63,94,0.05)" }}
        >
          {error}
        </Alert>
      )}

      {/* TERMINAL KAPSAYICISI (MAC GÖRÜNÜMÜ EKLENDİ) */}
      <Paper
        ref={terminalContainerRef}
        elevation={0}
        sx={{
          flexGrow: 1,
          bgcolor: "#030305",
          border: "1px solid rgba(234,240,248,0.08)",
          borderRadius: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column", // Mac üst barı ve terminali alt alta dizmek için
          boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
        }}
      >
        {/* SADECE GÖRSEL: Mac Stil Üst Pencere Çubuğu */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1.5,
            px: 2,
            bgcolor: "rgba(234,240,248,0.02)",
            borderBottom: "1px solid rgba(234,240,248,0.05)",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <DotIcon sx={{ fontSize: 12, color: "#FF5F56" }} />
            <DotIcon sx={{ fontSize: 12, color: "#FFBD2E" }} />
            <DotIcon sx={{ fontSize: 12, color: "#27C93F" }} />
          </Box>
          <Typography
            variant="caption"
            sx={{ ml: 2, color: "text.disabled", fontFamily: "monospace" }}
          >
            visione@{serverName.toLowerCase().replace(/\s/g, "")} ~ ssh
          </Typography>
        </Box>

        {/* Gerçek Terminal Render Alanı */}
        <Box sx={{ flexGrow: 1, p: 2, pt: 1, position: "relative" }}>
          <div ref={terminalRef} style={{ height: "100%", width: "100%" }} />
        </Box>
      </Paper>

      {/* BİLDİRİM (Kopyalandı / Yapıştırıldı) - GÖRÜNÜM GÜNCELLENDİ */}
      <Snackbar
        open={Boolean(snackbarMsg)}
        autoHideDuration={2000}
        onClose={() => setSnackbarMsg("")}
        message={snackbarMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          sx: {
            bgcolor: "primary.main",
            fontWeight: "bold",
            borderRadius: 2,
          },
        }}
      />
    </Box>
  );
}
